/**
 * Learning Hub — journey map of commercialisation stages, progress donut,
 * rich content rows (text, image+text, image with captions), next-section
 * map navigation, hidden Ctrl+Shift+E inline editor (Phase 4 — content from
 * Firestore + editor persistence). Prototype source: data-page="learning".
 * Content seeded from backend/seed/learning.json. British English.
 */
import { Fragment, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { TopNav } from '../components/TopNav';
import { ProgressRing } from '../components/ProgressRing';
import { Button } from '../components/Button';
import type { LearningSection, LearningRow } from '../types/learning';
import seedContent from '../../../backend/seed/learning.json';
import { useSession } from '../context/SessionContext';
import { api } from '../lib/api';
import { computeLearningPct } from '../lib/learningProgress';
import './Learning.css';

const sections = seedContent.sections as LearningSection[];

const JOURNEY_STEPS: Array<{
  main: string;
  branch?: string;
  branchTone?: 'turquoise';
  final?: boolean;
}> = [
  { main: 'research' },
  { main: 'design', branch: 'feedback', branchTone: 'turquoise' },
  { main: 'protection', branch: 'licensing' },
  { main: 'prototyping', branch: 'crowdfunding' },
  { main: 'manufacturing', branch: 'sell', final: true },
];

const ArrowRight = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

const ImageIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="3" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <path d="m21 15-5-5L5 21" />
  </svg>
);

export function Learning() {
  const navigate = useNavigate();
  const session = useSession();
  const { celebrations, learningCompletedModules, markCelebration, setLearningCompletedModules, setLearningPct } = session;
  const [completed, setCompleted] = useState<Set<string>>(() => new Set(learningCompletedModules));
  const [mapOpen, setMapOpen] = useState(false);
  const [completionOpen, setCompletionOpen] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const completedRef = useRef(completed);
  const readStart = useRef(new Map<string, number>());
  const celebrationTriggered = useRef(
    Boolean(celebrations.learn || (typeof localStorage !== 'undefined' && localStorage.getItem('itk_celebrated_learn') === 'true')),
  );
  const completionTimer = useRef<number | null>(null);

  const progressPct = useMemo(() => {
    return computeLearningPct([...completed]);
  }, [completed]);

  useEffect(() => {
    completedRef.current = completed;
    setLearningPct(progressPct);
    setLearningCompletedModules([...completed]);
  }, [completed, progressPct, setLearningCompletedModules, setLearningPct]);

  // Hydrate learning progress + celebration flag from the BFF on mount.
  useEffect(() => {
    let cancelled = false;
    api
      .get<{
        learningCompletedModules?: string[];
        celebrations?: { learn?: boolean };
      }>('/api/v1/me')
      .then((data) => {
        if (cancelled) return;
        if (data.learningCompletedModules && Array.isArray(data.learningCompletedModules) && data.learningCompletedModules.length > 0) {
          setCompleted(new Set(data.learningCompletedModules));
          setLearningCompletedModules(data.learningCompletedModules);
        }
        if (data.celebrations?.learn) {
          celebrationTriggered.current = true;
          markCelebration('learn');
          if (typeof localStorage !== 'undefined') localStorage.setItem('itk_celebrated_learn', 'true');
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setHydrated(true);
      });
    return () => {
      cancelled = true;
    };
  }, [markCelebration, setLearningCompletedModules]);

  // Autosave completed modules to the BFF when changed.
  useEffect(() => {
    if (!hydrated) return;
    const t = setTimeout(() => {
      api.put('/api/v1/me/learning', { completedModules: [...completed] }).catch(() => {});
    }, 600);
    return () => clearTimeout(t);
  }, [completed, hydrated]);

  const toggleDone = (id: string) => {
    setCompleted((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const scrollToSection = (id: string) => {
    document.getElementById(`itk-learn-${id}`)?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    let scheduled = false;

    const checkReadProgress = () => {
      scheduled = false;
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const now = Date.now();
      const newlyCompleted: string[] = [];

      sections.forEach((section) => {
        if (completedRef.current.has(section.id)) return;
        const element = document.getElementById(`itk-learn-${section.id}`);
        if (!element) return;
        const bounds = element.getBoundingClientRect();

        if (!readStart.current.has(section.id) && bounds.top >= -30 && bounds.top < viewportHeight) {
          readStart.current.set(section.id, now);
        }

        const startedAt = readStart.current.get(section.id);
        if (startedAt && now - startedAt > 1800 && bounds.bottom > 60 && bounds.bottom <= viewportHeight + 30) {
          newlyCompleted.push(section.id);
        }
      });

      if (newlyCompleted.length) {
        setCompleted((previous) => {
          const next = new Set(previous);
          newlyCompleted.forEach((id) => next.add(id));
          return next;
        });
      }
    };

    const onScroll = () => {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(checkReadProgress);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (
      progressPct < 100 ||
      celebrations.learn ||
      celebrationTriggered.current ||
      (typeof localStorage !== 'undefined' && localStorage.getItem('itk_celebrated_learn') === 'true')
    ) {
      return;
    }

    celebrationTriggered.current = true;
    markCelebration('learn');
    if (typeof localStorage !== 'undefined') localStorage.setItem('itk_celebrated_learn', 'true');
    api.post('/api/v1/me/celebrations', { learn: true }).catch(() => {});
    completionTimer.current = window.setTimeout(() => setCompletionOpen(true), 420);
  }, [celebrations.learn, markCelebration, progressPct]);

  useEffect(() => () => {
    if (completionTimer.current !== null) window.clearTimeout(completionTimer.current);
  }, []);

  const closeCompletionModal = useCallback(() => {
    celebrationTriggered.current = true;
    markCelebration('learn');
    if (typeof localStorage !== 'undefined') localStorage.setItem('itk_celebrated_learn', 'true');
    document.body.focus();
    setCompletionOpen(false);
  }, [markCelebration]);

  useEffect(() => {
    if (!mapOpen && !completionOpen) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (completionOpen) {
        closeCompletionModal();
      } else {
        setMapOpen(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = originalOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [closeCompletionModal, completionOpen, mapOpen]);

  const openMap = () => setMapOpen(true);
  const navigateFromMap = (id: string) => {
    setMapOpen(false);
    scrollToSection(id);
  };

  return (
    <>
      <TopNav
        title="Learning Hub"
        onBack={() => navigate('/')}
        action={
          <button type="button" className="itk-learning-map-nav" onClick={openMap} aria-label="Jump to commercialisation map">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.1} strokeLinecap="round" strokeLinejoin="round">
              <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" />
              <line x1="8" y1="2" x2="8" y2="18" />
              <line x1="16" y1="6" x2="16" y2="22" />
            </svg>
          </button>
        }
      />

      <div className="itk-wrap itk-wrap--learning">
        {/* ── HERO ── */}
        <section className="itk-section-box" id="itk-learn-hero">
          <div className="itk-hero-grid">
            <div>
              <div className="itk-overline">Idea Toolkit</div>
              <h1 className="itk-page-title">Learning Hub</h1>
              <p className="itk-sec-desc" style={{ marginBottom: 0 }}>
                Find out everything you need to know about developing an idea and commercialising it
                inside this interactive learning system.
              </p>
            </div>
            <ProgressRing percent={progressPct} size={172} stroke={16} label="complete" />
          </div>
        </section>

        {/* ── JOURNEY MAP ── */}
        <section className="itk-section-box">
          <div className="itk-overline">Commercialisation map</div>
          <h2 className="itk-sec-title">From idea to profit</h2>
          <p className="itk-sec-desc">
            Tap any module to jump to it. The circle on each module shows whether you&rsquo;ve completed it
            &mdash; it ticks itself when you read a module from top to bottom, or you can tap it to mark a
            module complete or incomplete yourself.
          </p>
          <JourneyMap completed={completed} onToggle={toggleDone} onNavigate={scrollToSection} />
        </section>

        {/* ── MODULE SECTIONS ── */}
        {sections.map((sec, si) => (
          <section
            key={sec.id}
            className="itk-section-box"
            id={`itk-learn-${sec.id}`}
            aria-label={sec.title}
          >
            <div className="itk-overline">Module {si + 1}</div>
            <h2 className="itk-sec-title">{sec.title}</h2>
            <p className="itk-sec-desc">{sec.desc}</p>

            <div className="itk-learn-rows">
              {sec.rows.map((row, ri) => (
                <LearningRow key={ri} row={row} />
              ))}
            </div>

            <div className="itk-next-block">
              <div className="itk-next-btns">
                <Button variant="secondary" pill className="itk-learning-map-button" onClick={openMap}>
                  View commercialisation map
                  <ArrowRight />
                </Button>
              </div>
            </div>
          </section>
        ))}

        {/* ── NEXT STEP (coach box) ── */}
        <section className="itk-section-box itk-coach-box">
          <div className="itk-overline">Next step</div>
          <h2 className="itk-sec-title">Book idea review</h2>
          <p className="itk-sec-desc itk-coach-desc">
            Arrange a free call with an experienced innovation coach to confidentially talk through your
            idea and map out the best route forward.
          </p>
          <div className="itk-coach-actions">
            <Button pill onClick={() => navigate('/book')}>Book idea review</Button>
            <Button variant="secondary" pill onClick={() => navigate('/idea')} className="itk-learning-dashboard-button">
              Go to idea dashboard
              <ArrowRight />
            </Button>
          </div>
        </section>

        <div className="itk-back-top-wrap">
          <Button variant="secondary" pill onClick={() => scrollToSection('itk-learn-hero')}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="19" x2="12" y2="5" />
              <polyline points="5 12 12 5 19 12" />
            </svg>
            Back to the top
          </Button>
        </div>
      </div>

      <div
        className={`itk-learning-map-modal ${mapOpen ? 'itk-learning-map-modal--open' : ''}`.trim()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="itk-learning-map-modal-title"
        aria-hidden={!mapOpen}
      >
        <div className="itk-learning-map-dialog">
          <button type="button" className="itk-learning-modal-close" onClick={() => setMapOpen(false)} aria-label="Close">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
          <div className="itk-overline">Commercialisation map</div>
          <h2 id="itk-learning-map-modal-title" className="itk-learning-modal-title">From idea to profit</h2>
          <p className="itk-learning-modal-subtitle">Tap any module to jump straight to it.</p>
          <JourneyMap completed={completed} onToggle={toggleDone} onNavigate={navigateFromMap} />
        </div>
      </div>

      <div
        className={`itk-learning-completion-modal ${completionOpen ? 'itk-learning-completion-modal--open' : ''}`.trim()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="itk-learning-completion-title"
        aria-hidden={!completionOpen}
      >
        <div className="itk-learning-completion-card">
          <button type="button" className="itk-learning-modal-close" onClick={closeCompletionModal} aria-label="Close">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
          <div className="itk-learning-completion-tick" aria-hidden="true">
            <svg viewBox="0 0 72 72">
              <circle cx="36" cy="36" r="34" />
              <path d="M21 37.5 31 47.5 51 26" />
            </svg>
          </div>
          <div id="itk-learning-completion-title" className="itk-learning-completion-title">Nice work!</div>
          <p className="itk-learning-completion-subtitle">You completed the Learning Hub</p>
          <div className="itk-learning-completion-actions">
            <Button
              pill
              autoFocus
              onClick={() => {
                closeCompletionModal();
                navigate('/book');
              }}
            >
              Discuss next steps
            </Button>
            <Button
              variant="secondary"
              pill
              onClick={() => {
                closeCompletionModal();
                navigate('/idea');
              }}
            >
              Go to Idea Dashboard
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}

interface JourneyMapProps {
  completed: Set<string>;
  onToggle: (id: string) => void;
  onNavigate: (id: string) => void;
}

function JourneyMap({ completed, onToggle, onNavigate }: JourneyMapProps) {
  const getSection = (id: string) => sections.find((section) => section.id === id);

  const renderNode = (id: string, type: 'main' | 'branch', turquoise = false, final = false) => {
    const section = getSection(id);
    if (!section) return null;
    const isComplete = completed.has(id);
    const classes = [
      'itk-jnode',
      type === 'main' ? 'itk-jnode--main' : 'itk-jnode--branch',
      turquoise ? 'itk-jnode--turquoise' : '',
      final ? 'itk-jnode--final' : '',
    ].filter(Boolean).join(' ');

    return (
      <button className={classes} type="button" onClick={() => onNavigate(id)}>
        <span
          className={`itk-jstat ${isComplete ? 'itk-jstat--done' : ''}`.trim()}
          title={isComplete ? 'Completed — tap to mark as not completed' : 'Not completed — tap to mark as completed'}
          role="checkbox"
          aria-checked={isComplete}
          aria-label={`${section.title} ${isComplete ? 'completed' : 'not completed'}`}
          onClick={(event) => {
            event.stopPropagation();
            onToggle(id);
          }}
          onKeyDown={(event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            event.stopPropagation();
            onToggle(id);
          }}
          tabIndex={0}
        >
          {isComplete && (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3.4} strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          )}
        </span>
        <span className="itk-jlbl">{section.title}</span>
      </button>
    );
  };

  return (
    <div className="itk-jmap" role="navigation" aria-label="Commercialisation map">
      {JOURNEY_STEPS.map((step, index) => (
        <Fragment key={step.main}>
          {index > 0 && <JourneyArrow direction="down" />}
          {step.final && <JourneyArrow direction="down" branchColumn />}
          {renderNode(step.main, 'main')}
          {step.branch && (
            <>
              <JourneyArrow direction="right" />
              {renderNode(step.branch, 'branch', step.branchTone === 'turquoise', step.final)}
            </>
          )}
        </Fragment>
      ))}
    </div>
  );
}

function JourneyArrow({ direction, branchColumn = false }: { direction: 'down' | 'right'; branchColumn?: boolean }) {
  return (
    <div className={`itk-jarrow itk-jarrow--${direction} ${branchColumn ? 'itk-jarrow--branch-column' : ''}`.trim()} aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round">
        {direction === 'down' ? <><path d="M12 4v14" /><polyline points="19 13 12 20 5 13" /></> : <><path d="M4 12h14" /><polyline points="13 5 20 12 13 19" /></>}
      </svg>
    </div>
  );
}

/** Render a single content row (text / imgtext / image). */
function LearningRow({ row }: { row: LearningRow }) {
  if (row.type === 'text') {
    return (
      <div className="itk-learn-row itk-learn-row--text">
        <div style={{ minWidth: 0 }}>
          {/* Content is trusted prototype HTML — already sanitised in the seed extraction. */}
          <div className="itk-rich" dangerouslySetInnerHTML={{ __html: row.html ?? '' }} />
        </div>
      </div>
    );
  }

  if (row.type === 'imgtext') {
    return (
      <div className="itk-learn-row itk-learn-row--imgtext">
        <figure>
          <div className="itk-img-box">
            <div className="itk-img-ph">
              <ImageIcon />
              <span>Image</span>
            </div>
          </div>
          {row.img?.caption && (
            <figcaption className="itk-img-cap">{row.img.caption}</figcaption>
          )}
        </figure>
        <div style={{ minWidth: 0 }}>
          <div className="itk-rich" dangerouslySetInnerHTML={{ __html: row.html ?? '' }} />
        </div>
      </div>
    );
  }

  // type === 'image'
  return (
    <div className="itk-learn-row itk-learn-row--image">
      <figure>
        <div className="itk-img-box">
          <div className="itk-img-ph">
            <ImageIcon />
            <span>Image</span>
          </div>
        </div>
        {row.img?.caption && <figcaption className="itk-img-cap">{row.img.caption}</figcaption>}
      </figure>
    </div>
  );
}